import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { PageService, PageType } from './services/page.service';
import { UmamiService } from "./services/umami.service";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { filter, takeUntil } from "rxjs/operators";
import { NgcCookieConsentService, NgcStatusChangeEvent } from "ngx-cookieconsent";
import { ScreeningRoomService } from "./shared/services/screening-room.service";
import { cookieConfig as baseConfig } from './app.cookie.config';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {

  type$!: Observable<PageType>;
  // TODO: Remove when Lux Award is finished
  private destroy$ = new Subject<void>();
  protected routeHasBeenAnalyzed:boolean = false;
  private sentryInitialized = false;
  private sentryInitializing = false;

  constructor(
    private readonly pageService: PageService,
    private readonly router: Router,
    private readonly umamiService: UmamiService,
    // TODO: Remove when Lux Award is finished
    private readonly activatedRoute: ActivatedRoute,
    // TODO: Remove when Lux Award is finished
    protected readonly screeningRoomService: ScreeningRoomService,
    private readonly cc: NgcCookieConsentService
  ) {
    this.type$ = this.pageService.type$;
  }

  // TODO: Remove when Lux Award is finished
  ngOnInit() {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        const { url, slug } = this.extractRouteData();
        const shouldActivate = this.screeningRoomService.isScreeningRoomRoute(url, slug);
        if (shouldActivate) {
          this.updateCookieBar();
        }
        this.screeningRoomService.setActivated(shouldActivate);
        this.routeHasBeenAnalyzed = true;

        if (this.cc.hasConsented() && !shouldActivate) {
          this.initSentry().then();
        }
      });

    this.cc.statusChange$.pipe(takeUntil(this.destroy$)).subscribe(async (event: NgcStatusChangeEvent) => {
      const isScreeningRoom = await firstValueFrom(this.screeningRoomService.activated$);

      if (event.status === 'allow' && !this.sentryInitialized && !isScreeningRoom) {
        this.initSentry().then();
      }
    });
  }

  async initSentry() {
    if (this.sentryInitialized || this.sentryInitializing) {
      return;
    }

    this.sentryInitializing = true;

    const Sentry = await import('@sentry/angular');

    Sentry.init({
      dsn: "https://d32f4a6f843880e37fa687c771157ec8@o4509111068327936.ingest.de.sentry.io/4509111071014992",
      // Adds request headers and IP for users, for more info visit:
      // https://docs.sentry.io/platforms/javascript/guides/angular/configuration/options/#sendDefaultPii
      sendDefaultPii: true,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration()
      ],
      tracesSampleRate: 1,
      tracePropagationTargets: ['localhost', /^https:\/\/uat.festivalscope\.com\/api/, /^https:\/\/pro.festivalscope\.com\/api/],
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      debug: false
    });

    this.sentryInitialized = true;
  }

  ngAfterViewInit(): void {
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd)
    ).subscribe(async () => {
      this.umamiService.trackPage();
    });
  }

  // TODO: Remove when Lux Award is finished
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get pageType(): typeof PageType {
    return PageType;
  }

  // TODO: Remove when Lux Award is finished
  private extractRouteData() {
    let route = this.activatedRoute;

    while (route.firstChild) {
      route = route.firstChild;
    }

    return {
      url: this.router.url,
      slug: route.snapshot.params['slug'] ?? null
    };
  }

  private updateCookieBar() {
    const newConfig = { ...baseConfig };

    newConfig.type = 'info';
    newConfig.palette.popup.background = '#084386';
    newConfig.palette.button.background = '#084386';
    newConfig.content.cookiePolicyHref = '/page/cookies-policy-lux-audience-award';
    newConfig.content.allow = 'Close';
    newConfig.content.dismiss = 'Close';
    newConfig.content.message = 'This platform uses essential cookies only to ensure secure access and proper functioning. These cookies do not require your consent.';
    newConfig.content.cookiePolicyLink = 'Learn more about the cookies here';

    this.cc.destroy();
    this.cc.init(newConfig);
  }
}
